import React, { useState, useEffect } from 'react';
import { useAuth } from '../services/AuthContext';
import { supabase } from '../services/supabase/supabase-config';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { buscarVersaoAtual, gerarLinkDownload, registrarDownload } from '../services/downloads';
import { podeBaixarPlugin, diasRestantes as calcularDiasRestantes } from '../utils/statusLicenca';

const DownloadsPage = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [versao, setVersao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [baixando, setBaixando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verificarAcesso = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Consulta direta em vez da Edge Function: aqui só decidimos o que
        // mostrar na tela. A Edge Function check-plugin-access é quem emite o
        // token assinado, e existe para o plugin, não para o site.
        //
        // A RLS já limita o resultado às assinaturas do próprio usuário, então
        // não é preciso (nem adianta) filtrar por user_id aqui.
        //
        // Mesma query do DashboardPage (status active/past_due, maior
        // current_period_end) — as duas telas usavam critérios diferentes antes
        // e podiam mostrar conclusões contraditórias sobre a mesma assinatura.
        // A elegibilidade pra baixar é decidida depois, com podeBaixarPlugin().
        const { data: assinaturas, error: erroAssinatura } = await supabase
          .from('subscriptions')
          .select('status, current_period_end, is_valid, plans(name)')
          .in('status', ['active', 'past_due'])
          .order('current_period_end', { ascending: false })
          .limit(1);

        if (erroAssinatura) throw erroAssinatura;

        setSubscription(assinaturas?.[0] ?? null);
        setVersao(await buscarVersaoAtual());
      } catch (err) {
        console.error('Erro ao verificar a assinatura:', err);
        setError(
          'Não foi possível verificar sua assinatura. Tente novamente mais tarde.'
        );
      } finally {
        setLoading(false);
      }
    };

    verificarAcesso();
  }, [user]);

  const canDownload = podeBaixarPlugin(subscription);
  const planName = subscription?.plans?.name ?? 'Nenhum';
  const diasRestantes = canDownload ? calcularDiasRestantes(subscription) : null;

  const baixar = async () => {
    if (!versao?.download_url) return;
    try {
      setBaixando(true);
      const link = await gerarLinkDownload(versao.download_url);
      await registrarDownload(versao.version);
      window.location.href = link;
    } catch (err) {
      console.error('Erro ao gerar o link de download:', err);
      setError(
        'Não foi possível gerar o link de download. Tente novamente em instantes.'
      );
    } finally {
      setBaixando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-rn-bg">
        <p className="text-rn-text-soft">Verificando sua assinatura...</p>
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }

  return (
    <div className="bg-rn-bg min-h-[calc(100vh-4rem)] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <header className="mb-8">
          <h1 className="text-[27px] md:text-[32px] font-semibold text-rn-navy">
            Downloads
          </h1>
          <p className="text-rn-text-soft mt-2">
            Faça o download da versão mais recente do plugin aqui.
          </p>
        </header>

        <Card hover={false} className="p-8">
          <h2 className="text-2xl font-semibold text-rn-navy mb-2">
            RNPlugin {versao?.version ? `v${versao.version}` : ''}
          </h2>
          <p className="text-rn-text-soft mb-4">Compatível com AutoCAD 2021-2024</p>

          <div className="text-sm mb-6">
            Seu plano:{' '}
            <span
              className={`font-semibold ${
                canDownload ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {planName}
            </span>
            {canDownload && diasRestantes !== null && (
              <span className="text-rn-text-soft">
                {' '}
                — {diasRestantes} dia{diasRestantes === 1 ? '' : 's'} restante
                {diasRestantes === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {canDownload && versao?.release_notes && (
            <div className="mb-6 p-4 bg-rn-bg rounded-lg">
              <h3 className="font-semibold text-rn-navy mb-2">
                Novidades desta versão
              </h3>
              <pre className="text-sm text-rn-text-soft whitespace-pre-wrap font-sans">
                {versao.release_notes}
              </pre>
            </div>
          )}

          {!canDownload && (
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
              <h3 className="font-bold text-yellow-800">Download bloqueado</h3>
              <p className="text-yellow-700 mt-1">
                Você não tem uma assinatura ativa. Verifique seu{' '}
                <Link to="/dashboard" className="underline font-semibold">
                  painel de controle
                </Link>
                .
              </p>
            </div>
          )}

          {canDownload && !versao && (
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
              <h3 className="font-bold text-yellow-800">
                Nenhuma versão disponível
              </h3>
              <p className="text-yellow-700 mt-1">
                Sua assinatura está ativa, mas ainda não há versão publicada para
                download. Tente novamente mais tarde.
              </p>
            </div>
          )}

          {canDownload && versao?.download_url && (
            <Button
              className="w-full md:w-auto"
              onClick={baixar}
              disabled={baixando}
            >
              {baixando ? 'Preparando o download...' : 'Baixar agora'}
            </Button>
          )}

          {canDownload && versao && (versao.sha256 || versao.virustotal_url) && (
            <div className="mt-6 pt-4 border-t border-rn-line text-xs text-rn-text-soft space-y-1">
              <p className="font-medium text-rn-navy">Verificação de segurança</p>
              <p>
                O instalador ainda não é assinado digitalmente — é normal o
                Windows ou o navegador avisarem antes de rodar. Confira abaixo
                para ter certeza do que está baixando.
              </p>
              {versao.sha256 && (
                <p className="break-all">
                  SHA256: <span className="font-mono">{versao.sha256}</span>
                </p>
              )}
              {versao.virustotal_url && (
                <p>
                  <a
                    href={versao.virustotal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-rn-navy"
                  >
                    Ver relatório do VirusTotal
                  </a>
                </p>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DownloadsPage;
